import {
  createElement,
  isValidElement,
  lazy,
  useEffect,
  useState,
  type ComponentProps,
  type ComponentType,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import { safeSessionStorage } from './safeStorage';

// Once we've reloaded once to recover a stale chunk, a flag in sessionStorage
// stops us from reloading again. This bounds an otherwise-infinite loop when
// the chunk is genuinely unreachable (truly missing on the host, the user is
// offline, or a browser extension is blocking the request) — in that case we
// let the rejection surface to the app's error boundary instead of reloading
// forever.
const RELOAD_FLAG = 'chunk-reload-attempted';

const preloaders = new Map<string, () => Promise<void>>();

export type LazyRoutePage<T extends ComponentType<any>> = FunctionComponent<
  ComponentProps<T>
> & {
  /** Starts the chunk ahead of the first render. Never rejects. */
  preload: () => Promise<void>;
};

/**
 * lazy() for route pages, recovering from the post-deploy stale-chunk failure.
 *
 * After a deploy the host removes the previous build's content-hashed chunks.
 * A browser still running the old build holds stale chunk URLs, so navigating
 * to a not-yet-loaded lazy route requests a file that now 404s and the
 * import() rejects. One full reload re-fetches index.html (browsers always
 * revalidate it) and with it the current chunk manifest.
 *
 * While the reload is in flight the failed import is replaced with a
 * forever-pending promise, so Suspense keeps showing <PageLoader> instead of
 * flashing the error boundary. Handling this globally via Vite's
 * `vite:preloadError` event + preventDefault() is NOT equivalent: it makes the
 * import() resolve with `undefined`, which React's lazy initializer then
 * dereferences and crashes on (Sentry RMP-WEB-38).
 *
 * `path` registers the page under the route the sidebar links to, so
 * prefetchRoute / scheduleIdlePrefetch can warm the chunk before the click.
 */
// Matches React's lazy() bound, so components with props work too.
export function lazyRoute<T extends ComponentType<any>>(
  loadPage: () => Promise<{ default: T }>,
  path?: string
): LazyRoutePage<T> {
  let Loaded: T | undefined;
  const remember = (module: { default: T }) => {
    Loaded = module.default;
    return module;
  };

  const Lazy = lazy(() =>
    loadPage().then(remember, (error: unknown) => {
      // Already reloaded once and it still failed → genuine problem. Let the
      // rejection surface to the error boundary.
      if (safeSessionStorage.getItem(RELOAD_FLAG) === 'true') {
        throw error;
      }
      // With storage denied the one-shot guard can't be recorded, so a
      // reload would loop forever — surface the error instead.
      if (!safeSessionStorage.setItem(RELOAD_FLAG, 'true')) {
        throw error;
      }
      window.location.reload();
      return new Promise<never>(() => {});
    })
  );

  // Not through `Lazy`: a stale-chunk failure here must not trigger the
  // reload — nobody is looking at the page yet — so it is swallowed. One
  // attempt only: the browser memoizes a failed module fetch, so a second
  // import() of the same URL rejects without a request, and the next real
  // navigation recovers through the reload above. That is why the callers
  // only preload on a good, online connection.
  let preloading: Promise<void> | undefined;
  const preload = () =>
    (preloading ??= loadPage()
      .then(remember)
      .then(
        () => {},
        () => {}
      ));
  if (path) preloaders.set(path, preload);

  function RoutePage(props: ComponentProps<T>) {
    // React.lazy suspends on its first render even when the import has already
    // resolved, and React then keeps the Suspense fallback up for 300ms. A page
    // that is already loaded renders directly instead. Chosen once per mount:
    // a page that committed through `Lazy` must not be swapped and remounted.
    const [Page] = useState<ComponentType<any>>(() => Loaded ?? Lazy);

    // A page on screen means chunks are current again — clear the flag so a
    // later deploy in the same session can recover with a fresh reload. Not on
    // a preload: that would reset the guard for a page that still fails.
    useEffect(() => {
      safeSessionStorage.removeItem(RELOAD_FLAG);
    }, []);

    return createElement(Page, props);
  }
  return Object.assign(RoutePage, { preload });
}

/**
 * Preloads the lazy pages `pathname` renders. Returns the target of a redirect
 * route on that branch.
 */
function preloadBranch(
  routes: RouteObject[],
  pathname: string
): string | undefined {
  let redirect: string | undefined;
  for (const { route } of matchRoutes(routes, pathname) ?? []) {
    const element = route.element;
    if (!isValidElement<{ to?: unknown }>(element)) continue;
    const { type, props } = element;
    if (type === Navigate) {
      if (typeof props.to === 'string') redirect = props.to;
    } else if (typeof type === 'function' && 'preload' in type) {
      void (type as LazyRoutePage<ComponentType>).preload();
    }
  }
  return redirect;
}

// "*" redirects to "/", which redirects to the home page.
const MAX_REDIRECTS = 3;

/**
 * Start the chunk of every page `pathname` renders, following redirect routes
 * to the page they land on. Called on document load, so the chunk downloads
 * beside the session check instead of after it.
 */
export function preloadRoutePages(routes: ReactNode, pathname: string): void {
  if (!connectionAllowsPrefetch()) return;
  const routeObjects = createRoutesFromChildren(routes);
  let target: string | undefined = pathname;
  for (let hop = 0; target && hop <= MAX_REDIRECTS; hop++) {
    target = preloadBranch(routeObjects, target);
  }
}

function warm(path: string): Promise<void> {
  return preloaders.get(path)?.() ?? Promise.resolve();
}

/** Warm a registered page's chunk ahead of navigation (hover, focus, touch). */
export function prefetchRoute(path: string): void {
  if (!connectionAllowsPrefetch()) return;
  void warm(path);
}

type ConnectionInfo = { saveData?: boolean; effectiveType?: string };

function connectionAllowsPrefetch(): boolean {
  if (navigator.onLine === false) return false;
  const { connection } = navigator as Navigator & {
    connection?: ConnectionInfo;
  };
  if (!connection) return true;
  return (
    !connection.saveData &&
    connection.effectiveType !== '2g' &&
    connection.effectiveType !== 'slow-2g'
  );
}

// Without requestIdleCallback there is no idleness signal; a few seconds
// gives the page just opened a head start on its own requests.
const FALLBACK_IDLE_DELAY_MS = 3000;

function whenIdle(fn: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => fn(), { timeout: 5000 });
  } else {
    setTimeout(fn, FALLBACK_IDLE_DELAY_MS);
  }
}

/**
 * Warm the given pages one at a time while the browser is idle, so a first
 * click never waits on a chunk. Sequential, so the prefetches never compete
 * with the current page's own requests. Returns a cancel for effect cleanup.
 */
export function scheduleIdlePrefetch(paths: string[]): () => void {
  const queue = paths.filter(path => preloaders.has(path));
  let cancelled = false;
  const next = () => {
    if (cancelled || !connectionAllowsPrefetch()) return;
    const path = queue.shift();
    if (!path) return;
    void warm(path).then(() => whenIdle(next));
  };
  whenIdle(next);
  return () => {
    cancelled = true;
  };
}
